import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  BookOpen,
  Brain,
  Feather,
  Lightbulb,
  Quote,
  Sparkles,
  Heart,
  AlertTriangle,
} from 'lucide-react';
import Navbar from '../components/Navbar';

/**
 * Sudoku Academy — Science & History page.
 *
 * EDITORIAL STANCE: Every claim on this page is either:
 *   (a) a historical fact with a reliable source
 *   (b) a correlational finding clearly framed as such
 *   (c) a well-supported neuroscience principle (e.g., neuroplasticity)
 *
 * We deliberately avoid overclaiming (no "Sudoku cures PTSD", no IQ-boost
 * promises). The FTC settled with Lumosity in 2016 over similar overclaims;
 * we do not repeat those mistakes. Where evidence is preliminary or
 * Sudoku-specific research is lacking, we say so.
 */

const REFS = [
  {
    id: 1,
    text:
      'Brooker, H., Wesnes, K.A., et al. (2019). "An online investigation of the relationship between the frequency of word puzzle use and cognitive function in a large sample of older adults." International Journal of Geriatric Psychiatry, 34(7), 932–940.',
    doi: '10.1002/gps.5085',
  },
  {
    id: 2,
    text:
      'Brooker, H., Wesnes, K.A., et al. (2019). "The relationship between the frequency of number-puzzle use and baseline cognitive function in a large online sample of adults aged 50 and over." International Journal of Geriatric Psychiatry, 34(7), 921–931.',
    doi: '10.1002/gps.5096',
  },
  {
    id: 3,
    text:
      'Hardy, J.L., Nelson, R.A., et al. (2015). "Enhancing Cognitive Abilities with Comprehensive Training: A Large, Online, Randomized, Active-Controlled Trial." PLOS ONE, 10(9).',
    doi: '10.1371/journal.pone.0134467',
  },
  {
    id: 4,
    text:
      'James, E.L., Bonsall, M.B., et al. (2015). "Computer Game Play Reduces Intrusive Memories of Experimental Trauma via Reconsolidation-Update Mechanisms." Psychological Science, 26(8), 1201–1215. (On Tetris specifically — visuospatial cognitive loading. Not Sudoku, but same cognitive family.)',
    doi: '10.1177/0956797615583071',
  },
  {
    id: 5,
    text:
      'Owen, A.M., Hampshire, A., et al. (2010). "Putting brain training to the test." Nature, 465, 775–778. (Cautionary: broad cognitive training did NOT transfer to untrained tasks in a 11,430-participant trial.)',
    doi: '10.1038/nature09042',
  },
  {
    id: 6,
    text:
      'Federal Trade Commission. (2016). "Lumos Labs to Pay $2 Million to Settle FTC Deceptive Advertising Charges for Its \'Lumosity\' Brain Training Program." Press release, January 5, 2016.',
    url:
      'https://www.ftc.gov/news-events/news/press-releases/2016/01/lumosity-pay-2-million-settle-ftc-deceptive-advertising-charges-its-brain-training-program',
  },
  {
    id: 7,
    text:
      'Hayashi, Y. (2013). "The Maki Kaji Interview: The Father of Sudoku." Nikoli Co., Ltd. Archived interviews and historical notes on the development of modern Sudoku.',
    url: 'https://www.nikoli.co.jp/en/',
  },
  {
    id: 8,
    text:
      'Delahaye, J.-P. (2006). "The Science Behind Sudoku." Scientific American, 294(6), 80–87.',
  },
];

function Ref({ ids }) {
  const list = Array.isArray(ids) ? ids : [ids];
  return (
    <sup className="text-[#8B5A3C] font-mono text-xs ml-0.5">
      [{list.join(',')}]
    </sup>
  );
}

export default function SciencePage() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-main)' }}>
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 py-10 leading-relaxed">
        <header className="mb-10">
          <div className="flex items-center gap-2 text-[#8B5A3C] text-sm font-mono mb-2">
            <BookOpen className="w-4 h-4" />
            <span>The science &amp; the story</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[var(--text-primary)] mb-3">
            Why Sudoku, really?
          </h1>
          <p className="text-[var(--text-secondary)] text-lg">
            A puzzle born in 18th-century Switzerland, given a Japanese name in the 1980s,
            and quietly studied by cognitive scientists ever since. Here's what we actually
            know — and what we don't.
          </p>
        </header>

        {/* Etymology + pronunciation */}
        <Section icon={Feather} title="The name" color="#34C759">
          <p className="text-[var(--text-secondary)]">
            <strong className="text-[var(--text-primary)]">Sudoku</strong> (数独) is a
            contraction of the Japanese phrase{' '}
            <em>Sūji wa dokushin ni kagiru</em> (数字は独身に限る) —{' '}
            <em>"the digits must remain single."</em>
          </p>
          <p className="text-[var(--text-secondary)] mt-3">
            Pronounced <strong>soo-DOH-koo</strong> in English, or closer to{' '}
            <strong>sūdoku</strong> (long first vowel) in Japanese. The name was coined by{' '}
            <strong>Maki Kaji</strong>, founder of the Japanese puzzle magazine{' '}
            <em>Nikoli</em>, in April 1984.<Ref ids={7} />
          </p>
        </Section>

        {/* History */}
        <Section icon={Quote} title="The history, in one paragraph" color="#8B5A3C">
          <p className="text-[var(--text-secondary)]">
            The mathematical ancestor of Sudoku — Latin Squares — was formalized by the Swiss
            mathematician Leonhard Euler in 1782. The modern puzzle form, originally titled{' '}
            <em>Number Place</em>, was created by an American architect named{' '}
            <strong>Howard Garns</strong> for Dell's <em>Pencil Puzzles and Word Games</em>{' '}
            magazine in 1979.<Ref ids={8} /> Garns never lived to see his invention become a
            global phenomenon — he died in 1989, five years after Kaji's Nikoli magazine
            rebranded it as Sudoku and just before it swept Japan. Britain's{' '}
            <em>The Times</em> printed its first Sudoku in November 2004, and within months
            the puzzle was everywhere.
          </p>
        </Section>

        {/* Technique naming */}
        <Section icon={Lightbulb} title="Where the strategy names come from" color="#FF9F0A">
          <p className="text-[var(--text-secondary)]">
            Most of the advanced technique names — <em>X-Wing, Swordfish, XY-Wing, W-Wing,
            Skyscraper, Kite, AIC</em> — emerged not from academia but from online Sudoku
            communities in the mid-2000s, particularly on forums like{' '}
            <em>Players' Forum (SudokuPlayers)</em> and the wiki{' '}
            <em>Sudopedia</em>. Contributors would spot a repeating grid pattern, name it
            for what it looked like, and the name stuck. Provenance is often murky: "X-Wing"
            is usually credited to the early Sudopedia contributors around 2005; "Alternating
            Inference Chain" is generally attributed to forum user{' '}
            <em>Myth Jellies</em> on the Sudoku Players Forum in 2007.
          </p>
          <p className="text-[var(--text-secondary)] mt-3 italic text-sm">
            If you're a historian with better attribution — please{' '}
            <Link to="/" className="text-[#8B5A3C] underline">get in touch</Link>. We're
            trying to honor the people who figured this stuff out.
          </p>
        </Section>

        {/* Cognitive science — honest */}
        <Section icon={Brain} title="What cognitive science actually says" color="#AF52DE">
          <p className="text-[var(--text-secondary)]">
            The largest study to date on number-puzzle use and cognition —{' '}
            <strong>19,078 adults</strong> aged 50+ in the UK PROTECT cohort — found that
            people who solved number puzzles <em>frequently</em> had measurably better
            performance on tests of grammatical reasoning, attention, and spatial working
            memory.<Ref ids={2} /> A parallel study on <em>word</em> puzzles in the same
            cohort replicated the pattern.<Ref ids={1} />
          </p>
          <p className="text-[var(--text-secondary)] mt-3">
            <strong className="text-[var(--text-primary)]">Important caveat:</strong>{' '}
            these are <em>correlational</em> findings. They cannot tell us whether puzzles
            make you sharper, or whether sharper people do more puzzles, or whether a third
            factor (education, income, health habits) explains both. The most famous
            randomized study of general "brain training"{' '}
            <em>— Owen et al., Nature 2010, n=11,430 —</em>{' '}
            found <strong>no</strong> transfer to untrained tasks after six weeks.<Ref ids={5} /> A
            second large online trial in 2015 did find modest transfer gains.<Ref ids={3} />{' '}
            The honest summary: <em>targeted</em> cognitive practice probably helps{' '}
            <em>that specific skill</em>, and the case for general IQ boosts remains weak.
          </p>
          <Disclaimer>
            In 2016, the FTC fined <em>Lumosity</em> $2 million for advertising broad IQ
            and dementia-prevention benefits their evidence didn't support.<Ref ids={6} /> We
            won't repeat that mistake. Sudoku Academy is a puzzle platform, not a medical
            device.
          </Disclaimer>
        </Section>

        {/* Therapeutic — very honest */}
        <Section icon={Heart} title="Puzzles, trauma, and the brain" color="#FF3B30">
          <p className="text-[var(--text-secondary)]">
            You may have read that Sudoku helps with PTSD or "reverses" trauma-related brain
            changes. Let's be careful here. There is <strong>no</strong> peer-reviewed
            randomized trial showing Sudoku specifically treats PTSD.
          </p>
          <p className="text-[var(--text-secondary)] mt-3">
            What <em>does</em> exist is adjacent research on <em>visuospatial</em>{' '}
            cognitive loading — puzzles that occupy the same mental resources used to
            rehearse traumatic memories. In a series of studies led by{' '}
            <strong>Emily Holmes</strong> at Oxford, playing{' '}
            <em>Tetris</em> shortly after a traumatic experience reduced the frequency of
            intrusive memories in the following week.<Ref ids={4} /> Sudoku is in the same{' '}
            <em>family</em> of tasks — sustained, attention-demanding, visuospatial — but
            it has not been tested specifically. It is plausible that similar mechanisms
            apply; it is not proven.
          </p>
          <p className="text-[var(--text-secondary)] mt-3">
            What <em>is</em> well-established:{' '}
            <strong>the adult brain remains plastic</strong>. Sustained engagement in
            effortful, novel cognitive work alters synaptic connectivity, and sleep
            consolidates those changes. These are textbook neuroscience principles, not
            Sudoku-specific claims. If a daily puzzle helps you engage, concentrate, and
            reach a quiet state for twenty minutes — that is a real, measurable thing your
            brain is doing. Whether it heals anything bigger is a question we don't yet
            have the evidence to answer.
          </p>
          <Disclaimer>
            If you're working through trauma, Sudoku is not a substitute for evidence-based
            care with a licensed clinician. If you'd like to read a careful review of what's
            known, search for "cognitive tasks and intrusive memories" in the peer-reviewed
            literature — start with Holmes &amp; James.
          </Disclaimer>
        </Section>

        {/* Closing — the tone */}
        <Section icon={Sparkles} title="Why we built this" color="#8B5A3C">
          <p className="text-[var(--text-secondary)]">
            Sudoku Academy exists because the best puzzles <em>teach</em>. Every level in
            our curriculum is gated to a specific deductive technique, and every puzzle is
            verified by a pure-logic solver — no guessing required, ever. The goal isn't to
            sell you a cognitive miracle. It's to hand you a 21st-century successor to Euler's
            Latin Squares, one technique at a time, and let your brain do what brains do.
          </p>
          <div className="mt-6 flex gap-3 flex-wrap">
            <Link to="/play">
              <Button
                className="bg-[#8B5A3C] hover:bg-[#A46944] text-white"
                data-testid="science-cta-play"
              >
                Start the curriculum
              </Button>
            </Link>
            <Link to="/tutorials">
              <Button
                variant="outline"
                className="border-[var(--border-subtle)]"
                data-testid="science-cta-tutorials"
              >
                Browse techniques
              </Button>
            </Link>
          </div>
        </Section>

        {/* References */}
        <section className="mt-12 pt-8 border-t border-[var(--border-subtle)]" data-testid="science-references">
          <h3 className="font-heading text-lg font-semibold text-[var(--text-primary)] mb-4">
            References
          </h3>
          <ol className="space-y-3 text-sm text-[var(--text-secondary)]">
            {REFS.map((r) => (
              <li key={r.id} className="flex gap-3">
                <span className="text-[#8B5A3C] font-mono flex-shrink-0">[{r.id}]</span>
                <span>
                  {r.text}
                  {r.doi && (
                    <>
                      {' '}
                      <a
                        href={`https://doi.org/${r.doi}`}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="text-[#8B5A3C] underline"
                      >
                        doi:{r.doi}
                      </a>
                    </>
                  )}
                  {r.url && (
                    <>
                      {' '}
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="text-[#8B5A3C] underline"
                      >
                        source
                      </a>
                    </>
                  )}
                </span>
              </li>
            ))}
          </ol>
        </section>
      </main>
    </div>
  );
}

function Section({ icon: Icon, title, color, children }) {
  return (
    <section className="mb-10" data-testid={`science-section-${title.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-3">
        <div
          className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${color}22` }}
        >
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <h2 className="font-heading text-xl font-bold text-[var(--text-primary)]">
          {title}
        </h2>
      </div>
      <div className="pl-10">{children}</div>
    </section>
  );
}

function Disclaimer({ children }) {
  return (
    <Card className="mt-4 bg-[#FF9F0A]/5 border-[#FF9F0A]/30">
      <CardContent className="p-4 flex items-start gap-3">
        <AlertTriangle className="w-4 h-4 text-[#FF9F0A] flex-shrink-0 mt-0.5" />
        <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{children}</p>
      </CardContent>
    </Card>
  );
}
